import * as _ from 'lodash';

/*
  The shapes of the simulation API (/admin/simulations), in one place: when the backend changes a shape, this is the file to change.

  Written from the backend's DTOs (legaleyse-backend `develop` at c1f416d: web/admin/simulations/dto) and checked against payloads
  captured from a running backend (../__fixtures__): the runs, the comparison and the error bodies from the branch of
  legaleyse-backend#101, which adds `scenarioRequests`, `activeRunId`, `runId`, `runStatus`, the code SIMULATION_RUN_NOT_COMPLETED and
  the ids of the changed players. The repository is plain JavaScript, so these are JSDoc typedefs, for the editor and the reader; the
  constants and helpers at the end are real.

  Two things hold for every payload below:
  - A 64-bit value (ids, seeds) is written as a string, because a JS number cannot hold all of them. Never turn one into a number.
    Counts are 32-bit and stay numbers.
  - A time is a LocalDateTime: ISO-8601 with no zone, such as '2026-10-17T07:45:09.207962'. It is UTC: the backend's README says that
    every date and time the admin API writes is, and that these are to be read as UTC and shown in the viewer's zone.
*/

/**
 * @typedef {string} Id A 64-bit id as the API writes it, such as '2111362866295607296'.
 * @typedef {string} LocalDateTime Such as '2026-10-17T07:45:09.207962': UTC, with no offset written.
 * @typedef {typeof RunStatus[keyof typeof RunStatus]} RunStatusValue
 * @typedef {typeof ScenarioParameterType[keyof typeof ScenarioParameterType]} ScenarioParameterTypeValue
 * @typedef {'ANTI_MONEY_LAUNDERING'|'RISK_ASSESSMENT'|'RESPONSIBLE_GAMBLING'|'FRAUD'} RuleCategoryValue
 * @typedef {'LOW'|'MEDIUM'|'HIGH'} AlertLevelValue
 */

/**
 * What an error answers with, whatever its status. `errorCode` is one of SimulationErrorCode when the backend gives one; a missing
 * run or workspace is a plain 404 and the screens go by the status.
 * @typedef {Object} ApiError
 * @property {string} error The message.
 * @property {string} [errorCode]
 * @property {Id} [activeRunId] Only for SIMULATION_LIMIT_REACHED, when a run that is queued or running is in the way: the one that
 *   has held the limit longest. Absent when the cause is the size of the run, which no other run is to blame for.
 * @property {Id} [runId] Only for the errors about the state of a run (SIMULATION_RUN_STATE, SIMULATION_RUN_NOT_COMPLETED): the run
 *   that is not in the state the call needed. For a comparison of two unfinished runs it is the baseline first.
 * @property {RunStatusValue} [runStatus] The state it is in, with runId.
 */

/**
 * A sandbox: a jurisdiction of type SIMULATION, copied from a production one. GET /simulations/workspaces
 * @typedef {Object} Workspace
 * @property {string} code Such as 'XM'. The server assigns it.
 * @property {string} name Such as 'Sandbox (MT)'.
 * @property {string} sourceCode The production jurisdiction its rules and risk configuration were copied from.
 * @property {LocalDateTime} createdAt
 * @property {LocalDateTime} lastRefreshedAt
 * @property {number} runCount The runs it holds, in any state: what maxRunsPerWorkspace counts.
 */

/**
 * POST /simulations/workspaces
 * @typedef {Object} CreateWorkspaceParams
 * @property {string} sourceCode
 */

/**
 * The limits of the simulation. GET /simulations/limits
 * @typedef {Object} Limits
 * @property {boolean} enabled False when the kill switch is on.
 * @property {number} maxEventsPerRun
 * @property {number} maxEventsPerSecond
 * @property {number} maxConcurrentRuns
 * @property {number} [maxRunsPerWorkspace] How many runs a sandbox keeps, in any state (legaleyse-backend#105). A run past it is
 *   refused with 409 SIMULATION_LIMIT_REACHED, with no activeRunId, until some are deleted; "Test this change" needs two places and is
 *   refused as a whole. A sandbox that already holds more keeps them, and cannot start another. Absent in a backend from before #105.
 * @property {number} runningRuns Runs that are queued or running now.
 */

/**
 * A parameter of a scenario. The type is one of ScenarioParameterType, and a type this build does not know is left out of the form.
 * @typedef {Object} ScenarioParameter
 * @property {string} key
 * @property {ScenarioParameterTypeValue} type
 * @property {number|null} min Only for the numeric types.
 * @property {number|null} max Only for the numeric types.
 * @property {number|boolean|string|null} default
 */

/**
 * A scenario of the catalog, which the form of a run is built from. GET /simulations/scenarios
 * @typedef {Object} Scenario
 * @property {string} id Such as 'DEPOSIT_REQUIRES_PEP_AND_SANCTION'.
 * @property {string} name
 * @property {string} description
 * @property {RuleCategoryValue} category
 * @property {string[]} ruleTypes The rules whose alerts the scenario expects.
 * @property {ScenarioParameter[]} parameters
 * @property {boolean} available False for a scenario that cannot be run now.
 * @property {string|null} unavailableReason Why, when it is not available.
 */

/**
 * A scenario in the request for a run, and in the run: `scenarioRequests`.
 * @typedef {Object} ScenarioRequest
 * @property {string} id
 * @property {Object<string, number|boolean|string>} [parameters] By key; the ones left out take their defaults. In a run it is always
 *   there, and is exactly what was asked for: empty for a scenario played with its defaults, and a value that was left out is not
 *   filled in with its default.
 */

/**
 * POST /simulations
 * @typedef {Object} CreateRunParams
 * @property {string} workspace The code of the sandbox to play in.
 * @property {number|string} [seed] The same seed gives the same events. Left out, the server picks one and the run reports it.
 * @property {string} [label]
 * @property {ScenarioRequest[]} scenarios At least one.
 */

/**
 * The answer to asking for a run (202): enough to follow it with.
 * @typedef {Object} RunCreated
 * @property {Id} id
 * @property {RunStatusValue} status
 */

/**
 * @typedef {Object} RunProgress
 * @property {number} eventsPlanned
 * @property {number} eventsGenerated
 * @property {number} eventsProcessed Events are processed after they are generated, so this is the one that says how far the run is.
 * @property {number} eventsFailed
 */

/**
 * @typedef {Object} RuleChange
 * @property {string} ruleType
 * @property {Object<string, string>} config The variables that are set; empty when only `enabled` changes.
 * @property {boolean|null} enabled Null when it is left as it is.
 */

/**
 * What a run changes in the rules of its workspace. Both runs of a comparison have one: the baseline's changes are empty.
 * @typedef {Object} RuleSetChanges
 * @property {RuleChange[]} rules
 * @property {Object<string, Object<string, number>>} riskRatios New ratios by category of risk (AML, RG, FRAUD), then by factor.
 */

/**
 * A run. The detail (GET /simulations/{id}) and the items of the list have the same fields, so the list needs no call for each row.
 * @typedef {Object} Run
 * @property {Id} id
 * @property {string} workspace The code of its sandbox.
 * @property {Id} brandId The brand the run's data belongs to: the run is reached by it, in the selector.
 * @property {string} brandName Such as 'SIM XM run 5'.
 * @property {Id} seed
 * @property {string|null} label
 * @property {RunStatusValue} status
 * @property {string[]} scenarios The ids of the scenarios, not their names: the names are in the catalog. In the order they were asked
 *   for, and the same id can be in it more than once (the generator seeds each scenario by its position).
 * @property {ScenarioRequest[]} scenarioRequests One for each of `scenarios`, in the same order, with the parameters that were given.
 *   A backend from before legaleyse-backend#90 does not send it.
 * @property {RunProgress} progress
 * @property {{total: number}} alerts
 * @property {{id: Id, name: string}} createdBy
 * @property {LocalDateTime} createdAt
 * @property {LocalDateTime|null} startedAt
 * @property {LocalDateTime|null} finishedAt
 * @property {string|null} error Why it failed, for a FAILED run.
 * @property {{id: Id, changes: RuleSetChanges|null}|null} ruleSet Null for a run played with the rules of its workspace.
 * @property {Id|null} baselineRunId The run this one is the variant of, in a comparison.
 */

/**
 * A page of runs. GET /simulations?page&pageSize&sortType&sortOrder (sortType ID, CREATED_AT or STATUS; sortOrder ASC or DESC)
 * @typedef {Object} RunsPage
 * @property {Run[]} result
 * @property {boolean} hasNext
 */

/**
 * @typedef {Object} RuleCount
 * @property {string} ruleType
 * @property {number} count
 */

/**
 * @typedef {Object} RuleAlerts
 * @property {string} ruleType
 * @property {RuleCategoryValue} category
 * @property {number} count
 * @property {number} open
 * @property {Object<AlertLevelValue, number>} byLevel
 */

/**
 * A scenario's outcome: the alerts it expected against the alerts the run raised.
 * @typedef {Object} ScenarioResult
 * @property {string} id
 * @property {RuleCount[]} expected
 * @property {RuleCount[]} actual
 * @property {RuleCount[]} tolerated Open alerts the scenario cannot rule out, which do not count against the match.
 * @property {boolean|null} match Null until the run has COMPLETED: show it as "not evaluated", never as a mismatch.
 */

/**
 * The results of a run. GET /simulations/{id}/results answers 200 with whatever the run has produced so far, for any status: the
 * counts are partial for an unfinished run, `match` is null, and a QUEUED run's counts are zero. A deleted run is a 404.
 * @typedef {Object} Results
 * @property {Id} runId
 * @property {RunStatusValue} status
 * @property {string|null} error
 * @property {{total: number, byType: Object<string, number>}} events
 * @property {{created: number}} players
 * @property {{total: number, open: number, byRuleType: RuleAlerts[]}} alerts
 * @property {Object[]} topRiskyPlayers Players, in the shape of the players of the dashboard.
 * @property {ScenarioResult[]} scenarios
 */

/**
 * A player whose outcome differs between the two runs of a comparison.
 * @typedef {Object} ChangedPlayer
 * @property {number} playerNumber The number of the player in the run.
 * @property {string} scenarioId
 * @property {string} baselineUsername
 * @property {string} variantUsername
 * @property {Id|null} baselinePlayerId For the page of the player in the baseline's brand. Null when that run has no player of this
 *   number (the event that registers them failed).
 * @property {Id|null} variantPlayerId The same, in the variant.
 * @property {string[]} baselineOpen Rule types with an alert open for them in the baseline.
 * @property {string[]} variantOpen
 * @property {string[]} gained Rule types open in the variant and not in the baseline.
 * @property {string[]} lost
 */

/**
 * Two runs of the same seed, one with a change to the rules, set side by side. GET /simulations/comparisons?baselineRunId&variantRunId&playerLimit
 * It is refused with 409 until both runs have COMPLETED: SIMULATION_RUN_STATE while one is queued or running (keep waiting), and
 * SIMULATION_RUN_NOT_COMPLETED when one ended as FAILED, CANCELLED or INTERRUPTED (it never will complete: stop). Both name the run.
 * @typedef {Object} RunComparison
 * @property {Id} baselineRunId
 * @property {Id} variantRunId
 * @property {Id} seed
 * @property {string} workspace
 * @property {number} players
 * @property {{total: number, open: number}} baseline
 * @property {{total: number, open: number}} variant
 * @property {Object[]} byRuleType The size of the difference first, then by rule type.
 * @property {number} playersChanged How many players differ, which may be more than `changedPlayers` lists.
 * @property {ChangedPlayer[]} changedPlayers By the number of the player, the lowest first: when `playerLimit` cuts the list, these are
 *   the first by number, not the ones that changed most.
 */

/* ---- Constants ---- */

export const RunStatus = Object.freeze({
    QUEUED: 'QUEUED',
    RUNNING: 'RUNNING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED',
    INTERRUPTED: 'INTERRUPTED'
});

const terminalStatuses = [
    RunStatus.COMPLETED,
    RunStatus.FAILED,
    RunStatus.CANCELLED,
    RunStatus.INTERRUPTED
];

const activeStatuses = [RunStatus.QUEUED, RunStatus.RUNNING];

/** Whether a run in this status is over: nothing more will change in it. */
export const isTerminalStatus = status => _.includes(terminalStatuses, status);

/** Whether a run in this status is queued or running. */
export const isActiveStatus = status => _.includes(activeStatuses, status);

/* The codes the backend gives in `errorCode` for the things a user can run into on these screens. */
export const SimulationErrorCode = Object.freeze({
    SIMULATION_DISABLED: 'SIMULATION_DISABLED',
    SIMULATION_LIMIT_REACHED: 'SIMULATION_LIMIT_REACHED',
    SIMULATION_RUN_STATE: 'SIMULATION_RUN_STATE',
    SIMULATION_RUN_NOT_COMPLETED: 'SIMULATION_RUN_NOT_COMPLETED',
    SIMULATION_READ_ONLY: 'SIMULATION_READ_ONLY',
    WORKSPACE_BUSY: 'WORKSPACE_BUSY',
    SANDBOX_NOT_ALLOWED: 'SANDBOX_NOT_ALLOWED'
});

export const ScenarioParameterType = Object.freeze({
    INTEGER: 'INTEGER',
    DECIMAL: 'DECIMAL',
    BOOLEAN: 'BOOLEAN',
    STRING: 'STRING'
});
